import { isRole } from "@thyroaid/shared";
import type { Session, SessionUser } from "../app/session/session";

/** The token fields the web app reads. Claims are set by the API only (RBAC-001). */
export interface TokenSnapshot {
  readonly uid: string;
  readonly displayName: string | null;
  readonly email: string | null;
  readonly claims: Readonly<Record<string, unknown>>;
}

/**
 * Maps a Firebase ID-token snapshot to the app session (AUTH-004). Same rules as the API
 * principal: unknown role claims grant nothing; MFA only from the second-factor claim.
 * UI decisions from this are cosmetic — the API re-verifies every request.
 */
export function sessionFromToken(token: TokenSnapshot | null): Session {
  if (!token) return { status: "anonymous" };
  const roles = Array.isArray(token.claims.roles)
    ? [...new Set(token.claims.roles.filter(isRole))]
    : [];
  const firebase = token.claims.firebase as { sign_in_second_factor?: unknown } | undefined;
  // Blank names (not just missing ones) fall back, so "" and "   " never render as a name.
  const name = token.displayName?.trim() ?? "";
  const email = token.email ?? "";
  const orgId = token.claims.orgId;
  const user: SessionUser = {
    uid: token.uid,
    displayName: name !== "" ? name : email !== "" ? email : "Signed-in user",
    roles,
    mfaVerified: typeof firebase?.sign_in_second_factor === "string",
    // Same rule as the API's principalFromClaims: unknown/empty grants nothing, never guessed at.
    orgId: typeof orgId === "string" && orgId.length > 0 ? orgId : null,
  };
  return { status: "authenticated", user };
}
